import React, { forwardRef, FC, useMemo, useRef } from "react";
import { CalendarIcon, ArrowRightIcon, XIcon } from "@heroicons/react/solid";
import DatePicker from "react-datepicker";
import {
  startOfDay,
  endOfDay,
  isAfter,
  isBefore,
  subMonths,
  subHours,
  subDays,
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "styles/datepicker.css";
import { Tooltip as ReactTooltip } from "react-tooltip";
import useIsMobile from "hooks/useIsMobile";
import { useAnalyticsFilters } from "providers/AnalyticsProvider/AnalyticsFilterProvider";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import { fmtDate } from "utils";
import { QuickSelectButton, CalendarHeader } from "./AnalyticsDatePickerComponents";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


// Types
interface QuickSelectOption {
  label: string;
  offset: (now: Date) => Date;
  requiresHourlyData?: boolean;
  requiresExtendedRange?: boolean;
}

interface ProcessedQuickSelectOption {
  label: string;
  offset: (now: Date) => Date;
  disabled: boolean;
  isHourly: boolean;
  tooltip?: string;
}

// Quick select date range options
const QUICK_SELECT_OPTIONS: QuickSelectOption[] = [
  { label: "Last hour", offset: (now: Date) => subHours(now, 1), requiresHourlyData: true },
  { label: "Last 3 hours", offset: (now: Date) => subHours(now, 3), requiresHourlyData: true },
  { label: "Last 12 hours", offset: (now: Date) => subHours(now, 12), requiresHourlyData: true },
  { label: "Last 24 hours", offset: (now: Date) => subHours(now, 24), requiresHourlyData: true },
  { label: "Last 2 days", offset: (now: Date) => subDays(now, 2) },
  { label: "Last week", offset: (now: Date) => subDays(now, 7) },
  { label: "Last month", offset: (now: Date) => subMonths(now, 1) },
  { label: "Last 3 months", offset: (now: Date) => subMonths(now, 3), requiresExtendedRange: true },
  { label: "Last 6 months", offset: (now: Date) => subMonths(now, 6), requiresExtendedRange: true },
];

interface DatePickerInputProps {
  value?: string;
  onClick?: () => void;
  startDate: Date | null;
  endDate: Date | null;
  onClear: () => void;
  className?: string;
}

interface CalendarContainerProps {
  children: React.ReactNode;
}



const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  ({ onClick, startDate, endDate, onClear, className }, ref) => {
    const isMobile = useIsMobile();
    const dateFormat: Intl.DateTimeFormatOptions = {
      weekday: undefined,
      month: "short",
      day: "numeric",
      year: isMobile ? undefined : "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const handleDatePickerOnClose = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      e.stopPropagation();
      onClear();
    };

    return (
      <div
        ref={ref}
        onClick={onClick}
        className={`flex items-center border border-gray-300 rounded px-3 text-sm cursor-pointer min-w-[280px] gap-2 shadow-sm bg-white ${
          className ?? "h-10 w-full"
        }`}
      >
        <CalendarIcon className="w-4 h-4 text-gray-500" />
        {startDate && (
          <>
            <span className="whitespace-nowrap">{fmtDate(startDate, dateFormat)}</span>
            {endDate && (
              <>
                <ArrowRightIcon className="w-4 h-4 mx-1 text-gray-500" />
                <span className="whitespace-nowrap">{fmtDate(endDate, dateFormat)}</span>
              </>
            )}
            <XIcon
              className="w-4 h-4 ml-auto text-gray-400 hover:text-gray-600"
              onClick={handleDatePickerOnClose}
            />
          </>
        )}
      </div>
    );
  }
);

DatePickerInput.displayName = "DatePickerInput";

type AnalyticsDatePickerProps = {
  className?: string;
  inputClassName?: string;
};

const AnalyticsDatePicker: FC<AnalyticsDatePickerProps> = ({
  className = "w-full",
  inputClassName = "h-10 w-full",
}) => {
  const { dateRange, setDateRange } = useAnalyticsFilters();
  const { activeSubscription } = useActiveSubscription();

  const [startDate, endDate] = dateRange;
  const datePickerRef = useRef<DatePicker>(null);

  // Get analytics entitlements from subscription
  // Mirrors the server's enforceAnalyticsHistoryLimit; keep the two in step.
  const ANALYTICS_MAX_MONTHS = 6;
  const analyticsComplete = activeSubscription?.hasFullAnalytics ?? false;
  const analyticsMaxMonths = analyticsComplete ? ANALYTICS_MAX_MONTHS : 1;

  // Calculate min and max dates based on plan
  const { minSelectableDate, maxSelectableDate } = useMemo(() => {
    return {
      minSelectableDate: startOfDay(subMonths(new Date(), analyticsMaxMonths)),
      maxSelectableDate: new Date(),
    };
  }, [analyticsMaxMonths]);

  // Filter quick select options based on plan
  const availableQuickSelectOptions = useMemo<ProcessedQuickSelectOption[]>(() => {
    const now = new Date();

    return QUICK_SELECT_OPTIONS.map((option) => {
      const offsetDate = option.offset(now);

      // Hourly and extended-range presets both require full analytics access.
      // The entitlement is binary (analyticsComplete), so they gate identically.
      const requiresHourly = option.requiresHourlyData ?? false;
      const requiresFullAccess = requiresHourly || (option.requiresExtendedRange ?? false);

      const isAvailable = !requiresFullAccess || analyticsComplete;

      // Check if the date is within the selectable range
      const isInRange = !isBefore(offsetDate, minSelectableDate);

      const disabled = !isAvailable || !isInRange;

      return {
        label: option.label,
        offset: option.offset,
        disabled,
        isHourly: requiresHourly
      };
    });
  }, [analyticsComplete, minSelectableDate]);

  const startOfCurrentMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);

  const handleRangeChange = (update: [Date | null, Date | null]) => {
    // Limited plans select via the entitlement-gated presets only — manual
    // calendar selection is disabled to close the date-entitlement bypass.
    if (!analyticsComplete) return;

    const [start, end] = update;

    // Validate both ends against the plan's look-back window
    if (start && isBefore(start, minSelectableDate)) return;
    if (end && isAfter(end, maxSelectableDate)) return;

    // For date picker selections, normalize to start/end of day
    const normalizedStart = start ? startOfDay(start) : null;
    const normalizedEnd = end ? endOfDay(end) : null;
    setDateRange([normalizedStart, normalizedEnd]);
    trackAnalyticsActionClicked({ section: 'global_all', action: 'date_range_change', target: 'date_picker'});
  };

  const handleQuickSelect = (offset: (now: Date) => Date, isHourly: boolean = false) => {
    const now = new Date();
    const endDate = isHourly ? now : endOfDay(now);
    setDateRange([offset(now), endDate]);
    // Close the date picker after selection
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(false);
    }

    trackAnalyticsActionClicked({ section: 'global_all', action: 'quick_select', target: 'date_picker'});
  };

  const handleReset = () => {
    const resetStart = isBefore(startOfCurrentMonth, minSelectableDate)
      ? minSelectableDate
      : startOfCurrentMonth;
    const endDate = new Date();
    setDateRange([startOfDay(resetStart), endOfDay(endDate)]);
    // Close the date picker after reset
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(false);
    }
    trackAnalyticsActionClicked({ section: 'global_all', action: 'reset_date_range', target: 'date_picker'})
  };

  const CalendarContainer: FC<CalendarContainerProps> = ({ children }) => (
    <div className="flex">
      <div className="flex flex-col justify-between border-r text-sm pr-4">
        <div className="flex flex-col space-y-1">
          {availableQuickSelectOptions.map(
            ({ label, offset, disabled, isHourly }) => (
              <QuickSelectButton
                key={label}
                label={label}
                onClick={() => !disabled && handleQuickSelect(offset, isHourly)}
                disabled={disabled}
              />
            )
          )}
        </div>
        <QuickSelectButton label="Reset" onClick={handleReset} isReset />
      </div>
      <div>
        {children}
        {!analyticsComplete && (
          <ReactTooltip
            id="analytics-date-upgrade"
            anchorSelect=".react-datepicker__day--in-range, .react-datepicker__day--range-start, .react-datepicker__day--range-end"
            className="!bg-brandDarkest !dark:bg-primary-800 !rounded-none max-w-sm !z-[9999]"
            float
            place="top"
          >
            <p className="text-sm">Upgrade to unlock custom date ranges.</p>
          </ReactTooltip>
        )}
      </div>
    </div>
  );

  // Custom day class name to gray out unavailable dates. Limited plans grey the
  // whole grid — they pick via presets only.
  const dayClassName = (date: Date) => {
    if (
      !analyticsComplete ||
      isBefore(date, minSelectableDate) ||
      isAfter(date, maxSelectableDate)
    ) {
      return "react-datepicker__day--disabled";
    }
    return "";
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <DatePicker
        ref={datePickerRef}
        selectsRange
        monthsShown={2}
        startDate={startDate}
        endDate={endDate}
        onChange={(update) =>
          handleRangeChange(update as [Date | null, Date | null])
        }
        dateFormat="MMM d, yyyy HH:mm"
        calendarContainer={CalendarContainer}
        renderCustomHeader={(props) => <CalendarHeader {...props} />}
        minDate={minSelectableDate}
        maxDate={maxSelectableDate}
        dayClassName={dayClassName}
        filterDate={(date) =>
          analyticsComplete &&
          !isBefore(date, minSelectableDate) &&
          !isAfter(date, maxSelectableDate)
        }
        /* KEY: width lives on the wrapper */
        wrapperClassName="w-full sm:w-[360px]"
        customInput={
          <DatePickerInput
            startDate={startDate}
            endDate={endDate}
            onClear={handleReset}
            className={inputClassName}
          />
        }
        popperClassName="bg-white border border-gray-300 shadow rounded"
      />
    </div>
  );
};

export default AnalyticsDatePicker;
